/* eslint-disable react-hooks/exhaustive-deps */

import React, { useContext, useState } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { ChargebeeSubscription } from 'modules/billing/lib/prices'
import useUserSegmentations from 'modules/onboarding/hooks/userSegmentations/useUserSegmentations'
import type Plan from 'modules/onboarding/lib/pricing/plans/Plan'
import getPlans, { CustomPlanDTO } from 'modules/onboarding/lib/pricing/plans/getPlans'
import type { VariantsEnum } from 'modules/onboarding/lib/pricing/plans/types'
import { ENTERPRISE_VARIANT, YEAR_PERIOD } from 'modules/onboarding/lib/pricing/plans/utils'
import { PeriodUnit } from 'modules/onboarding/lib/pricing/types'

import { useDatoPlans } from '../DatoPlansContext'

type PricingContextType = {
  allPlans: CustomPlanDTO
  togglePlanSelect: (plan: Plan) => void
  setVariantForPlan: (variant: VariantsEnum, plan: Plan) => void
  period: PeriodUnit
  setPeriod: (period: PeriodUnit) => void
  isYearly: () => boolean
  isCoreEnterpriseMandatory: () => boolean
  computePerSeatPrice: () => number
  computeTotalPrice: (seats: number) => number
  getSubscriptionItems: (seats: number) => ChargebeeSubscription[]
  isComparisonPlanSidebarOpen: boolean
  toggleComparisonPlanSidebar: (open: boolean) => void
}

type PricingProviderProps = {
  children: React.ReactNode | React.ReactNode[]
}

const PricingContext = React.createContext<PricingContextType>({
  allPlans: {
    normalPlans: [],
    corePlans: [],
    comparisonPlans: [],
  },
  togglePlanSelect: () => {},
  setVariantForPlan: () => {},
  period: 'month',
  setPeriod: () => {},
  isYearly: () => false,
  isCoreEnterpriseMandatory: () => false,
  computePerSeatPrice: () => 0,
  computeTotalPrice: () => 0,
  getSubscriptionItems: () => [],
  isComparisonPlanSidebarOpen: false,
  toggleComparisonPlanSidebar: () => {},
})

export const PricingProvider = ({ children }: PricingProviderProps) => {
  const { datoPlans, country } = useDatoPlans()
  const { interestingSections } = useUserSegmentations()
  const [isComparisonTableSidebarOpen, setIsComparisonTableSidebarOpen] = useState(false)
  const [period, setPeriod] = useState<PeriodUnit>('month')
  const [allPlans, setAllPlans] = useState<CustomPlanDTO>({
    normalPlans: [],
    corePlans: [],
    comparisonPlans: [],
  })

  const isYearly = () => {
    return period === YEAR_PERIOD
  }

  const isCoreEnterpriseMandatory = () => {
    if (!datoPlans) return false

    const enterprisePlanCondition = (plan: Plan) =>
      plan.isSelected && plan.activeVariantType === ENTERPRISE_VARIANT

    // If core is already enterprise, 0 dramas
    if (allPlans.corePlans.find(enterprisePlanCondition)) {
      return false
    }

    // Otherwise, we need to check if any module is enterprise
    if (allPlans.normalPlans.find(enterprisePlanCondition)) {
      return true
    }

    return false
  }

  const togglePlanSelect = (plan: Plan) => {
    plan.toggleSelection()
    if (plan.isSelected) plan.setActiveVariant('Business')
    setAllPlans({ ...allPlans } as CustomPlanDTO)
  }

  const setVariantForPlan = (variant: VariantsEnum, plan: Plan) => {
    allPlans.corePlans.concat(allPlans.normalPlans).forEach((auxPlan) => {
      if (plan.internalTitle === auxPlan.internalTitle) {
        auxPlan.setActiveVariant(variant)
        return
      }
    })

    setAllPlans({ ...allPlans } as CustomPlanDTO)
  }

  const computePrice = (priceHandler: (plan: Plan) => number | undefined) => {
    return allPlans.corePlans.concat(allPlans.normalPlans).reduce((acc, cur) => {
      if (cur?.isSelected) {
        const price = priceHandler(cur) || 0

        return acc + price
      }

      return acc
    }, 0)
  }

  const computePerSeatPrice = () => {
    return computePrice((cur) => cur.pricing.getSeatsPrice(period))
  }

  const computeTotalPrice = (seats: number) => {
    return computePrice((cur) => cur.pricing.getTotalPrice(period, seats))
  }

  const getSubscriptionItems = (seats: number) => {
    return allPlans.corePlans
      .concat(allPlans.normalPlans)
      .map((plan) => {
        if (!plan.isSelected) return null

        return {
          plan: { planId: plan.pricing.getPlanId(period) },
          quantity: plan.pricing.isPerSeat(period) ? seats : 1,
        }
      })
      .filter(Boolean) as ChargebeeSubscription[]
  }

  useUnsafeEffect(() => {
    if (datoPlans) {
      const plans = getPlans(datoPlans, country)
      plans.corePlans.forEach((corePlan) => corePlan.select())
      setAllPlans(plans)
    }
  }, [datoPlans])

  useUnsafeEffect(() => {
    if (!interestingSections.length || !allPlans.normalPlans) return
    allPlans.normalPlans.map((plan) => {
      if (plan.segmentationOption && interestingSections.includes(plan.segmentationOption)) {
        togglePlanSelect(plan)
        setVariantForPlan('Business', plan)
      }
    })
  }, [interestingSections, allPlans?.normalPlans])

  return (
    <PricingContext.Provider
      value={{
        allPlans,
        togglePlanSelect,
        setVariantForPlan,
        period,
        setPeriod,
        isYearly,
        isCoreEnterpriseMandatory,
        computePerSeatPrice,
        computeTotalPrice,
        getSubscriptionItems,
        toggleComparisonPlanSidebar: (open: boolean) => setIsComparisonTableSidebarOpen(open),
        isComparisonPlanSidebarOpen: isComparisonTableSidebarOpen,
      }}
    >
      {children}
    </PricingContext.Provider>
  )
}

export const usePricingContext = () => useContext(PricingContext)
